import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '@/utils/api';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { ArrowLeft, Zap, CheckCircle, Ticket, Gift } from 'lucide-react';

const DrawControl = () => {
  const [preview, setPreview] = useState(null);
  const [drawResult, setDrawResult] = useState(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);

  useEffect(() => {
    loadPreview();
  }, []);

  const loadPreview = async () => {
    try {
      const { data } = await api.get('/draw/preview');
      setPreview(data);
    } catch (error) {
      toast.error('Failed to load draw preview');
    }
  };

  const handleStartDraw = async () => {
    if (!preview?.can_draw) {
      toast.error('Cannot start draw. Check entries and prizes availability.');
      return;
    }

    setIsDrawing(true);
    try {
      const { data } = await api.post('/draw/start');
      setDrawResult(data);
      toast.success('Draw completed! Please confirm the result.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start draw');
    } finally {
      setIsDrawing(false);
    }
  };

  const handleConfirmDraw = async () => {
    if (!drawResult) return;

    if (!window.confirm('Are you sure you want to confirm this draw result?')) return;

    setIsConfirming(true);
    try {
      await api.post('/draw/confirm', drawResult);
      toast.success('Draw confirmed successfully!');
      setDrawResult(null);
      loadPreview();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to confirm draw');
    } finally {
      setIsConfirming(false);
    }
  };

  const handleCancelDraw = () => {
    if (window.confirm('Are you sure you want to cancel this draw?')) {
      setDrawResult(null);
      toast.info('Draw cancelled');
    }
  };

  return (
    <div className="min-h-screen bg-[#080808] pb-12">
      <div className="bg-[#121212] border-b border-[#D4AF37]/20 px-6 py-4 mb-6">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/admin/dashboard">
              <Button variant="outline" size="sm" className="border-[#D4AF37]/30 text-[#D4AF37]">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
            </Link>
            <h1 className="text-xl font-bold text-white">Draw Control</h1>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 space-y-6">
        {/* Preview Status */}
        {preview && (
          <Card className="bg-[#121212] border border-[#D4AF37]/10 p-8">
            <h2 className="text-2xl font-bold text-white mb-6">Draw Status</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <div className="text-center">
                <Ticket className="w-12 h-12 mx-auto mb-3 text-[#D4AF37]" />
                <div className="text-3xl font-bold text-[#D4AF37]">{preview.active_entries}</div>
                <div className="text-sm text-[#A0A0A0] mt-1">Active Entries</div>
              </div>
              <div className="text-center">
                <Gift className="w-12 h-12 mx-auto mb-3 text-[#F3A6C8]" />
                <div className="text-3xl font-bold text-[#F3A6C8]">{preview.available_prizes}</div>
                <div className="text-sm text-[#A0A0A0] mt-1">Available Prizes</div>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center">
                  {preview.can_draw ? (
                    <CheckCircle className="w-12 h-12 text-green-500" />
                  ) : (
                    <span className="text-4xl">⚠️</span>
                  )}
                </div>
                <div className={`text-lg font-bold ${preview.can_draw ? 'text-green-500' : 'text-red-500'}`}>
                  {preview.can_draw ? 'Ready to Draw' : 'Cannot Draw'}
                </div>
                <div className="text-sm text-[#A0A0A0] mt-1">
                  {preview.can_draw ? 'All systems go!' : 'Check entries and prizes'}
                </div>
              </div>
            </div>

            {!drawResult && (
              <div className="text-center">
                <Button
                  onClick={handleStartDraw}
                  disabled={!preview.can_draw || isDrawing}
                  className="px-12 py-6 text-lg rounded-full bg-[#D4AF37] text-[#080808] hover:bg-[#F3A6C8] hover:text-[#080808] transition-colors duration-200 shadow-[0_0_20px_rgba(212,175,55,0.4)] hover:shadow-[0_0_30px_rgba(243,166,200,0.6)] disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="btn-start-draw"
                >
                  <Zap className="w-6 h-6 mr-3" />
                  {isDrawing ? 'Drawing...' : 'Start Draw'}
                </Button>
              </div>
            )}
          </Card>
        )}

        {/* Draw Result */}
        {drawResult && (
          <Card className="bg-gradient-to-br from-[#D4AF37]/20 to-[#F3A6C8]/20 border-2 border-[#D4AF37] p-8">
            <div className="text-center mb-8">
              <Badge className="bg-[#D4AF37] text-[#080808] text-lg px-6 py-2 mb-4">
                Draw #{drawResult.draw_number}
              </Badge>
              <h2 className="text-3xl font-bold text-white mb-2">Draw Result</h2>
              <p className="text-[#A0A0A0]">Please review and confirm the result below</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
              {/* Winner */}
              <Card className="bg-[#121212] border border-[#D4AF37]/30 p-6">
                <div className="text-center">
                  <div className="text-sm uppercase tracking-wide text-[#D4AF37] mb-3 font-semibold">
                    Winner
                  </div>
                  <div className="text-2xl font-bold text-white mb-2" data-testid="draw-result-username">
                    {drawResult.username || 'No Username'}
                  </div>
                  <div className="text-xl text-[#F3A6C8] font-mono" data-testid="draw-result-sugo-id">
                    {drawResult.sugo_id}
                  </div>
                </div>
              </Card>

              {/* Prize */}
              <Card className="bg-[#121212] border border-[#F3A6C8]/30 p-6">
                <div className="text-center">
                  <div className="text-sm uppercase tracking-wide text-[#F3A6C8] mb-3 font-semibold">
                    Prize Won
                  </div>
                  <Gift className="w-12 h-12 mx-auto mb-3 text-[#D4AF37]" />
                  <div className="text-2xl font-bold text-white" data-testid="draw-result-prize">
                    {drawResult.prize_name}
                  </div>
                </div>
              </Card>
            </div>

            <div className="flex gap-4 justify-center">
              <Button
                onClick={handleConfirmDraw}
                disabled={isConfirming}
                className="px-8 py-4 text-lg rounded-full bg-green-600 text-white hover:bg-green-700 transition-colors duration-200"
                data-testid="btn-confirm-draw"
              >
                <CheckCircle className="w-5 h-5 mr-2" />
                {isConfirming ? 'Confirming...' : 'Confirm Result'}
              </Button>
              <Button
                onClick={handleCancelDraw}
                variant="outline"
                className="px-8 py-4 text-lg rounded-full border-red-500 text-red-500 hover:bg-red-500/10"
                data-testid="btn-cancel-draw"
              >
                Cancel
              </Button>
            </div>
          </Card>
        )}

        {/* Instructions */}
        <Card className="bg-[#121212] border border-[#D4AF37]/10 p-6">
          <h3 className="text-lg font-bold text-white mb-4">Instructions</h3>
          <ul className="space-y-2 text-[#A0A0A0] text-sm">
            <li className="flex items-start gap-2">
              <span className="text-[#D4AF37] font-bold">1.</span>
              Ensure there are active entries and available prizes before starting a draw.
            </li>
            <li className="flex items-start gap-2">
              <span className="text-[#D4AF37] font-bold">2.</span>
              Click "Start Draw" to randomly select a winner and prize.
            </li>
            <li className="flex items-start gap-2">
              <span className="text-[#D4AF37] font-bold">3.</span>
              Review the draw result carefully. The selected entry and prize are shown.
            </li>
            <li className="flex items-start gap-2">
              <span className="text-[#D4AF37] font-bold">4.</span>
              Click "Confirm Result" to finalize. This will mark the entry as Used and reduce the prize quantity.
            </li>
            <li className="flex items-start gap-2">
              <span className="text-[#D4AF37] font-bold">5.</span>
              You can cancel if you notice any issues. No data will be changed until confirmation.
            </li>
          </ul>
        </Card>

        {/* Link to Live Draw */}
        <Card className="bg-gradient-to-r from-[#D4AF37]/20 to-[#F3A6C8]/20 border border-[#D4AF37]/30 p-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-bold text-white mb-1">Full-Screen Live Draw Display</h3>
              <p className="text-[#A0A0A0] text-sm">Open the live draw page optimized for streaming and OBS</p>
            </div>
            <Link to="/live-draw" target="_blank">
              <Button className="bg-[#D4AF37] text-[#080808] hover:bg-[#F3A6C8]">
                <Zap className="w-4 h-4 mr-2" />
                Open Live Draw
              </Button>
            </Link>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default DrawControl;
